/**
 * Constructs a className string by ensuring no duplicates and filtering out empty values.
 * This is useful for combining multiple class names into a single string.
 *
 * @param {Array} classNames - Array of class names to be combined.
 * @returns {String} - A string with class names joined by spaces.
 */
export function constructClassName(classNames = []) {
  return [...new Set(classNames)] // Remove duplicates by converting to a Set
    .filter((className) => className && className !== "") // Remove falsy or empty values
    .join(" "); // Join the remaining valid class names with spaces
}

/**
 * Generates a label for a form item, optionally marking it as required.
 * This function is typically used to dynamically create form item labels based on input parameters.
 *
 * @param {Object} param - An object containing label and required properties.
 * @param {String} param.label - The label text to be displayed.
 * @param {Boolean} param.required - A flag indicating whether the label should be marked as required.
 * @returns {String|null} - Returns a formatted label if a label exists, null if no label is provided.
 */
export function getFormItemLabel({ label, required }) {
  if (!label) return null; // If no label is provided, return null
  if (label && !required) return label; // If label exists and is not required, return the label as it is
  return `<span label={label} required={required} />`; // Format the label with a required indicator
}

/**
 * Determines the current theme based on the provided isDark flag.
 * This is useful for applying dark or light mode styles in the application.
 *
 * @param {Boolean} isDark - A flag indicating whether the dark theme is enabled.
 * @returns {String} - Returns 'dark' if isDark is true, 'light' otherwise.
 */
export function getCurrentTheme(isDark) {
  return isDark ? "dark" : "light"; // Return 'dark' or 'light' based on the isDark flag
}
